import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Rocket } from 'lucide-react';
import { useMcpServers } from '../integrations/mcpServerRegistry';
import {
    Budget,
    Completion,
    Delivery,
    Routing,
    DraftFields,
    EMPTY_SERVICE_TOOLS,
    RoutineLimits,
    RoutineSpec,
    ServiceDetail,
    ServiceStatusBadge,
    ServiceTools,
    ServiceVersion,
    apiErrorMessage,
    describePublication,
    listingPayload,
    mergeSeededMcp,
    publicationLabel,
    slugifyKey,
} from './servicesShared';
import BuildTab from './editor/BuildTab';
import SetupTab from './editor/SetupTab';
import TeamTab from './editor/TeamTab';
import ImprovementTab from './editor/ImprovementTab';
import JobsList from '../jobs/JobsList';
import ListingTab, { ListingFields, SaveIndicator, SaveState } from './editor/ListingTab';
import { EditableStateField, fieldsToStateSchema, stateSchemaToFields } from './editor/StateSchemaEditor';
import { EditableSetupQuestion, editableToSetup, setupToEditable } from './editor/SetupQuestionsEditor';

interface AgentOption {
    id: string;
    name: string;
}

type EditorTab = 'build' | 'setup' | 'team' | 'jobs' | 'improvement' | 'listing';
const EDITOR_TABS: EditorTab[] = ['build', 'setup', 'team', 'jobs', 'improvement', 'listing'];
const TAB_LABELS: Record<EditorTab, string> = { build: 'Build', setup: 'Setup', team: 'Team', jobs: 'Jobs', improvement: 'Improvement', listing: 'Listing' };

const listingFromDetail = (detail: ServiceDetail): ListingFields => ({
    internal_name: detail.internal_name || '',
    name: detail.name || '',
    short_description: detail.short_description || '',
    description: detail.description || '',
    acquisition: detail.acquisition || 'vendor_assigned',
    visible: Boolean(detail.visible),
    scope: detail.scope || 'tenant',
    default_job_mode: detail.default_job_mode || 'live',
    start_on_events: detail.start_on_events || [],
    steward_user_ids: detail.steward_user_ids || [],
    guidance_defaults: detail.guidance_defaults || {},
});

/**
 * Service editor (OWNED_JOBS_PLAN.md §5 Vendor). Vertical nav, six tabs by
 * question, not by storage: Build (what the job does, incl. the customer agent
 * that speaks for it), Setup (how it starts and when it wakes), Team (your
 * people: who answers, how they are notified), Jobs (every customer it runs
 * for), Improvement (playbook, versions) and Listing (what the customer sees).
 * Two lifecycles underneath: versioned definition fields (saved to the draft,
 * frozen by Publish) and live fields (saved per change).
 */
const ServiceEditor: React.FC = () => {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const { get, post, put, remove } = useApi();
    const { servers } = useMcpServers();

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [detail, setDetail] = useState<ServiceDetail | null>(null);
    const serviceId = detail?.id ?? (isNew ? null : id!);

    // Service-level
    const [name, setName] = useState('');
    const [existingKey, setExistingKey] = useState<string | null>(null);
    const [status, setStatus] = useState<'active' | 'disabled'>('active');
    const [sourceAgentId, setSourceAgentId] = useState<string | null>(null);

    // Draft (versioned job definition)
    const [instructions, setInstructions] = useState('');
    const [guardrails, setGuardrails] = useState('');
    const [tools, setTools] = useState<ServiceTools>(EMPTY_SERVICE_TOOLS);
    const [stateFields, setStateFields] = useState<EditableStateField[]>([]);
    const [headline, setHeadline] = useState('');
    const [routines, setRoutines] = useState<RoutineSpec[]>([]);
    const [routineLimits, setRoutineLimits] = useState<RoutineLimits>({});
    const [setup, setSetup] = useState<EditableSetupQuestion[]>([]);
    const [completion, setCompletion] = useState<Completion>('standing');
    const [goal, setGoal] = useState('');
    const [budget, setBudget] = useState<Budget>({});
    const [delivery, setDelivery] = useState<Delivery>({});
    const [routing, setRouting] = useState<Routing>({});
    const [dirty, setDirty] = useState(false);

    // Listing (live, auto-saved)
    const [listing, setListing] = useState<ListingFields | null>(null);
    const [listingSaveState, setListingSaveState] = useState<SaveState>('idle');
    const listingRef = useRef<ListingFields | null>(null);
    listingRef.current = listing;
    const listingTimerRef = useRef<number | null>(null);
    const listingSeqRef = useRef(0);

    const [agents, setAgents] = useState<AgentOption[]>([]);
    const [seeding, setSeeding] = useState(false);
    const [commerceEnabled, setCommerceEnabled] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab: EditorTab = EDITOR_TABS.includes(tabParam as EditorTab) ? (tabParam as EditorTab) : 'build';
    const setActiveTab = (next: EditorTab) => {
        const params = new URLSearchParams(searchParams);
        if (next === 'build') params.delete('tab');
        else params.set('tab', next);
        setSearchParams(params, { replace: true });
    };

    useEffect(() => {
        get('/agents/')
            .then((response) => {
                // /agents/ lists the DRAFT rows the dashboard edits; the service
                // stores the LIVE agent (parent) id. Key the options on the live id
                // so a saved or seeded value always matches an option.
                const seen = new Set<string>();
                const options: AgentOption[] = [];
                for (const agent of response.data || []) {
                    const liveId: string = agent.parent_agent_id || agent.id;
                    if (seen.has(liveId)) continue;
                    seen.add(liveId);
                    options.push({ id: liveId, name: agent.name });
                }
                setAgents(options);
            })
            .catch((error) => console.error('Error fetching agents:', error));
        get('/services/commerce/stripe/account')
            .then((response) => setCommerceEnabled(!!response.data?.enabled))
            .catch(() => setCommerceEnabled(false));
        if (!isNew && id) void fetchService(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const applyVersion = (version: ServiceVersion | null | undefined) => {
        setInstructions(version?.instructions || '');
        setGuardrails(version?.guardrails || '');
        setTools({ ...EMPTY_SERVICE_TOOLS, ...(version?.tools || {}), write_policy: { ...(version?.tools?.write_policy || {}) } });
        setStateFields(stateSchemaToFields(version?.state_schema));
        setHeadline(version?.state_schema?.headline || '');
        setRoutines(version?.routines || []);
        setRoutineLimits(version?.routine_limits || {});
        setSetup(setupToEditable(version?.setup));
        setCompletion(version?.completion || 'standing');
        setGoal(version?.goal || '');
        setBudget(version?.budget || {});
        setDelivery(version?.delivery || {});
        setRouting(version?.routing || {});
    };

    const applyDetail = (next: ServiceDetail, { keepDraft = false }: { keepDraft?: boolean } = {}) => {
        setDetail(next);
        setName(next.internal_name || '');
        setExistingKey(next.key);
        setStatus(next.status === 'disabled' ? 'disabled' : 'active');
        setSourceAgentId(next.source_agent_id || null);
        if (!keepDraft) {
            // The editor always shows the draft when one exists; otherwise the
            // published definition (the next edit creates a draft from it).
            applyVersion(next.draft || next.published);
            setDirty(false);
        }
        setListing(listingFromDetail(next));
        setListingSaveState('idle');
    };

    const fetchService = async (serviceIdArg: string) => {
        try {
            setLoading(true);
            const response = await get(`/services/${serviceIdArg}`);
            applyDetail(response.data);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to load service'));
            navigate('/services');
        }
        setLoading(false);
    };

    const markDirty = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => (value: React.SetStateAction<T>) => {
        setter(value);
        setDirty(true);
    };
    const setInstructionsDirty = markDirty(setInstructions);
    const setGuardrailsDirty = markDirty(setGuardrails);
    const setToolsDirty = markDirty(setTools);
    const setStateFieldsDirty = markDirty(setStateFields);
    const setHeadlineDirty = markDirty(setHeadline);
    const setRoutinesDirty = markDirty(setRoutines);
    const setRoutineLimitsDirty = markDirty(setRoutineLimits);
    const setSetupDirty = markDirty(setSetup);
    const setCompletionDirty = markDirty(setCompletion);
    const setGoalDirty = markDirty(setGoal);
    const setBudgetDirty = markDirty(setBudget);
    const setDeliveryDirty = markDirty(setDelivery);
    const setRoutingDirty = markDirty(setRouting);

    const handleSeedFromAgent = async (agentId: string) => {
        try {
            setSeeding(true);
            const response = await get(`/services/seed/${agentId}`);
            const seeded: Partial<ServiceTools> = response.data.tools || {};
            setToolsDirty((prev) => ({
                ...prev,
                ...seeded,
                mcp: mergeSeededMcp(prev.mcp, seeded.mcp),
                write_policy: { ...prev.write_policy },
            }));
            setSourceAgentId(response.data.source_agent_id);
            toast.success(`Tools seeded from ${response.data.source_agent_name}`);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to seed from agent'));
        }
        setSeeding(false);
    };

    const previewKey = useMemo(() => existingKey || slugifyKey(name), [existingKey, name]);

    const draftPayload = (): DraftFields => ({
        instructions,
        guardrails,
        tools,
        state_schema: fieldsToStateSchema(stateFields, headline),
        routines,
        routine_limits: routineLimits,
        setup: editableToSetup(setup),
        completion,
        goal: completion === 'ai' ? goal.trim() || null : null,
        budget,
        delivery,
        routing,
    });

    /** Save service-level fields + the draft. Returns the service id. */
    const handleSave = async (): Promise<string | null> => {
        if (!name.trim()) {
            toast.error('A service name is required');
            return null;
        }
        if (completion === 'ai' && !goal.trim()) {
            toast.error('A bounded job needs a goal');
            return null;
        }
        setSaving(true);
        try {
            if (serviceId) {
                if (sourceAgentId !== (detail?.source_agent_id || null)) {
                    await put(`/services/${serviceId}`, sourceAgentId ? { source_agent_id: sourceAgentId } : { clear_source_agent: true });
                }
                const response = await put(`/services/${serviceId}/draft`, draftPayload());
                applyDetail(response.data);
                toast.success('Draft saved');
                return serviceId;
            }
            const response = await post('/services/', {
                name: name.trim(),
                key: existingKey || slugifyKey(name) || undefined,
                source_agent_id: sourceAgentId,
                ...draftPayload(),
            });
            applyDetail(response.data);
            toast.success('Service created');
            navigate(`/services/${response.data.id}`, { replace: true });
            return response.data.id;
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to save service'));
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        const savedId = dirty || !serviceId ? await handleSave() : serviceId;
        if (!savedId) return;
        setPublishing(true);
        try {
            const response = await post(`/services/${savedId}/publish`, {});
            applyDetail(response.data);
            toast.success(`Published v${response.data.published_version_number} — every job follows it at its next wake`);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to publish'));
        } finally {
            setPublishing(false);
        }
    };

    const handleDiscardDraft = async () => {
        if (!serviceId) return;
        try {
            const response = await remove(`/services/${serviceId}/draft`);
            applyDetail(response.data);
            toast.success('Draft discarded');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to discard draft'));
        }
        setDiscardConfirmOpen(false);
    };

    /** Persist the CURRENT listing. Live policy: the local state stays the
     * source of truth so an in-flight save can't clobber typing; a sequence
     * counter drops stale responses. */
    const persistListing = useCallback(async () => {
        const current = listingRef.current;
        if (!serviceId || !current) return;
        const seq = ++listingSeqRef.current;
        setListingSaveState('saving');
        try {
            const response = await put(`/services/${serviceId}`, listingPayload(current));
            if (seq !== listingSeqRef.current) return;
            setDetail(response.data);
            setName(response.data.internal_name || '');
            setListingSaveState('saved');
        } catch (error) {
            if (seq !== listingSeqRef.current) return;
            setListingSaveState('error');
            toast.error(apiErrorMessage(error, 'Listing change not saved'));
        }
    }, [put, serviceId]);

    const updateListing = (patch: Partial<ListingFields>) => {
        setListing((prev) => (prev ? { ...prev, ...patch } : prev));
        if (listingTimerRef.current !== null) window.clearTimeout(listingTimerRef.current);
        listingTimerRef.current = window.setTimeout(() => {
            listingTimerRef.current = null;
            void persistListing();
        }, 700);
    };

    const handleStatusChange = async (next: 'active' | 'disabled') => {
        setStatus(next);
        if (!serviceId) return;
        try {
            const response = await put(`/services/${serviceId}`, { status: next });
            setDetail(response.data);
            toast.success(next === 'active' ? 'Service enabled' : 'Service disabled — no new jobs');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to update status'));
        }
    };

    const handleRestore = async (version: ServiceVersion) => {
        if (!serviceId) return;
        try {
            const response = await post(`/services/${serviceId}/versions/${version.id}/restore`, {});
            applyDetail(response.data);
            toast.success(`v${version.version_number} restored into the draft`);
            setActiveTab('build');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to restore version'));
        }
    };

    const handleDelete = async () => {
        if (!serviceId) return;
        try {
            await remove(`/services/${serviceId}`);
            toast.success('Service archived');
            navigate('/services');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to archive service'));
        }
    };

    if (loading) {
        return (
            <div className="p-12 grow">
                <div className="animate-pulse space-y-4 max-w-3xl">
                    <div className="h-10 bg-muted rounded w-1/3" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                    <div className="h-64 bg-muted rounded" />
                </div>
            </div>
        );
    }

    const hasDraft = Boolean(detail?.draft) || dirty || !detail;
    const publicationState = detail ? publicationLabel({ ...detail, has_draft: hasDraft }) : 'unpublished';
    const isDraftTab = activeTab === 'build' || activeTab === 'setup' || activeTab === 'team' || activeTab === 'listing';

    return (
        <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
            <div className={activeTab === 'jobs' ? 'max-w-7xl' : 'max-w-5xl'}>
                <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" onClick={() => navigate('/services')}>
                    <ArrowLeft className="h-4 w-4" /> Services
                </button>

                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-bold flex items-center gap-3">
                            {isNew && !serviceId ? 'New Service' : detail?.name || name || 'Service'}
                            {serviceId ? <ServiceStatusBadge status={publicationState} /> : null}
                            {serviceId && status === 'disabled' ? <ServiceStatusBadge status="disabled" /> : null}
                        </h1>
                        <p className="mt-4 text-sm text-slate-500">
                            {detail
                                ? `${describePublication({ ...detail, has_draft: hasDraft })} · ${detail.jobs_on} job${detail.jobs_on === 1 ? '' : 's'} on${
                                      detail.jobs_blocked ? `, ${detail.jobs_blocked} waiting` : ''
                                  }. Edits go to the draft; publishing freezes a version every job follows at its next wake.`
                                : 'A job the AI owns for each customer and keeps working: instructions, guardrails, tools, state, routines, and how it starts and ends.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {(isDraftTab || !serviceId) && (
                            <>
                                <Button variant="outline" onClick={handleSave} disabled={saving || publishing}>
                                    {saving ? 'Saving…' : serviceId ? 'Save draft' : 'Create'}
                                </Button>
                                <Button onClick={handlePublish} disabled={saving || publishing || (!dirty && !detail?.draft && !isNew)}>
                                    <Rocket className="h-4 w-4 mr-1" />
                                    {publishing ? 'Publishing…' : 'Publish'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                {isDraftTab && detail?.draft && detail.published && (
                    <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                        <span>You have unpublished changes. Jobs keep running v{detail.published_version_number} until you publish.</span>
                        <Button variant="ghost" size="sm" onClick={() => setDiscardConfirmOpen(true)}>
                            Discard draft
                        </Button>
                    </div>
                )}

                {!serviceId && <Separator className="my-6" />}

                <div className={serviceId ? 'mt-6 flex items-start gap-8' : ''}>
                {serviceId && (
                    <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as EditorTab)} orientation="vertical" className="shrink-0 sticky top-0">
                        <TabsList className="flex h-auto w-44 flex-col items-stretch justify-start gap-1 rounded-lg border bg-card p-2">
                            {EDITOR_TABS.map((tab) => (
                                <TabsTrigger key={tab} value={tab} className="justify-start">
                                    {TAB_LABELS[tab]}
                                    {tab === 'jobs' && detail?.jobs_on ? <span className="ml-auto text-xs text-muted-foreground">{detail.jobs_on}</span> : null}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                )}
                <div className="min-w-0 flex-1">

                {(activeTab === 'build' || !serviceId) && (
                    <BuildTab
                        isNew={!serviceId}
                        name={name}
                        onNameChange={setName}
                        previewKey={previewKey}
                        keyLocked={Boolean(existingKey)}
                        onSourceAgentChange={(agentId) => {
                            setSourceAgentId(agentId);
                            setDirty(true);
                        }}
                        instructions={instructions}
                        onInstructionsChange={setInstructionsDirty}
                        guardrails={guardrails}
                        onGuardrailsChange={setGuardrailsDirty}
                        tools={tools}
                        onToolsChange={setToolsDirty}
                        servers={servers}
                        agents={agents}
                        sourceAgentId={sourceAgentId}
                        seeding={seeding}
                        onSeedFromAgent={handleSeedFromAgent}
                        stateFields={stateFields}
                        onStateFieldsChange={setStateFieldsDirty}
                        headline={headline}
                        onHeadlineChange={setHeadlineDirty}
                        completion={completion}
                        onCompletionChange={setCompletionDirty}
                        goal={goal}
                        onGoalChange={setGoalDirty}
                        budget={budget}
                        onBudgetChange={setBudgetDirty}
                    />
                )}

                {serviceId && activeTab === 'setup' && (
                    <SetupTab
                        setup={setup}
                        onSetupChange={setSetupDirty}
                        routines={routines}
                        onRoutinesChange={setRoutinesDirty}
                        limits={routineLimits}
                        onLimitsChange={setRoutineLimitsDirty}
                    />
                )}

                {serviceId && activeTab === 'team' && listing && (
                    <>
                        <div className="flex justify-end -mb-6">
                            <SaveIndicator state={listingSaveState} />
                        </div>
                        <TeamTab
                            stewardUserIds={listing.steward_user_ids}
                            onStewardsChange={(ids) => updateListing({ steward_user_ids: ids })}
                            guidanceDefaults={listing.guidance_defaults}
                            onGuidanceDefaultsChange={(next) => updateListing({ guidance_defaults: next })}
                            delivery={delivery}
                            onDeliveryChange={setDeliveryDirty}
                        />
                    </>
                )}

                {serviceId && activeTab === 'jobs' && detail && (
                    <section className="mb-10">
                        <JobsList
                            service={detail}
                            onServiceChanged={() => {
                                if (serviceId) void fetchService(serviceId);
                            }}
                        />
                    </section>
                )}

                {serviceId && activeTab === 'improvement' && detail && (
                    <ImprovementTab service={detail} playbook={detail.playbook || []} hasDraft={Boolean(detail.draft)} onRestore={handleRestore} />
                )}

                {serviceId && activeTab === 'listing' && listing && (
                    <ListingTab
                        serviceId={serviceId}
                        listing={listing}
                        onListingChange={updateListing}
                        saveState={listingSaveState}
                        commerceEnabled={commerceEnabled}
                        status={status}
                        onStatusChange={handleStatusChange}
                        jobsOn={detail?.jobs_on || 0}
                        onDelete={() => setDeleteConfirmOpen(true)}
                        routing={routing}
                        onRoutingChange={setRoutingDirty}
                    />
                )}
                </div>
                </div>
            </div>

            <Dialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
                <DialogContent className="max-w-sm">
                    <DialogTitle>Discard unpublished changes?</DialogTitle>
                    <DialogDescription>The draft goes back to v{detail?.published_version_number}. Published versions are untouched.</DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDiscardConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDiscardDraft}>
                            Discard
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Archive “{detail?.name || name}”?</DialogTitle>
                        <DialogDescription>
                            Every job of this service is turned off and the service disappears from lists. Job history is kept.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Archive
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ServiceEditor;
